/**
 * DivisionGamesList — a collapsible list of all games in a division (the pre-built
 * round-robin fixtures plus any scheduled/played ones). Collapsed by default;
 * expanding fetches the division's games on demand. Rendered under the standings
 * table on the Divisions page.
 *
 * Each game renders with the shared {@link GameSummaryRow} banner so it looks and
 * reads exactly like the Games page and Home page. The division is the section
 * heading, so the per-game subtitle omits it (date · time · venue · court). With
 * a viewer context the banner also offers the contextual action: "Propose" on a
 * still-unclaimed fixture the viewer is in, or "Approve" on one whose proposal
 * awaits their response.
 */
import React, { useState } from 'react';
import type { Game } from '@sulis-liiga/common';
import gameService from '../../services/gameService';
import GameSummaryRow, { toGameRowProps } from '../../components/game/GameSummaryRow';
import { gameToRowInput, type BannerContext } from '../../utils/gameBanner';

const EMPTY_CTX: BannerContext = { myTeamIds: new Set() };

const DivisionGamesList: React.FC<{
  divisionId: string;
  /** Viewer context for participant-specific actions (Propose / Approve). */
  bannerCtx?: BannerContext;
  /** One-click approval of a single-slot proposal. */
  onApproveProposal?: (proposalId: string) => void;
}> = ({ divisionId, bannerCtx = EMPTY_CTX, onApproveProposal }) => {
  const [expanded, setExpanded] = useState(false);
  const [games, setGames] = useState<Game[] | null>(null);
  const [loading, setLoading] = useState(false);

  const toggle = async () => {
    const next = !expanded;
    setExpanded(next);
    // Fetch once, on first expand.
    if (next && games === null) {
      setLoading(true);
      try {
        setGames(await gameService.getAllGames({ divisionId }));
      } catch {
        setGames([]);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="mt-3">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={expanded}
        className="text-sm font-medium text-primary-600 hover:text-primary-700"
      >
        {expanded ? '▾' : '▸'} Games{games ? ` (${games.length})` : ''}
      </button>

      {expanded && (
        <div className="mt-2 bg-white rounded-md border border-gray-100 divide-y divide-gray-100">
          {loading && <div className="px-4 py-3 text-sm text-gray-400">Loading…</div>}
          {!loading && games && games.length === 0 && (
            <div className="px-4 py-3 text-sm text-gray-500">No games in this division yet.</div>
          )}
          {!loading &&
            games &&
            games.map(g => (
              <GameSummaryRow
                key={g.id}
                {...toGameRowProps(
                  gameToRowInput(g, bannerCtx, { showDivision: false, showDate: true }),
                )}
                onApproveProposal={onApproveProposal}
              />
            ))}
        </div>
      )}
    </div>
  );
};

export default DivisionGamesList;
